/**
 * SCK-635 slice B — Settings → Security → Google sign-in.
 *
 * Shows the caller's current Google-linkage state and lets them link
 * (or re-link) Google to their existing account.
 *
 * Read state:  GET  /api/me/link-google
 *              → 200 { linked: bool, googleEmail?: string, linkedAt?: iso }
 *              A 404 is treated as "endpoint not deployed yet" — we
 *              render an unknown state and still show the button so
 *              the user can attempt linking. Any other failure is
 *              surfaced as an inline alert.
 * Link:        POST /api/me/link-google { googleIdToken }
 *              204 on success. Backend-specific errors bucket into
 *              email_mismatch / email_unverified / invalid_token; each
 *              gets copy the user can act on. Any other error surfaces
 *              the raw message.
 *
 * Uses fetch() directly rather than helpers/actions.js so the SCK-635
 * slice B interceptor (401 google_link_required → /login redirect)
 * cannot fire on the very page trying to fix the condition.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { API_URL } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";

const LINK_ERROR_COPY = {
  email_mismatch:
    "The Google account you signed in with does not match the email on your platform account. Sign in to Google with the same address and try again.",
  email_unverified:
    "That Google account is not verified yet. Verify the email with Google, then retry.",
  invalid_token:
    "The Google sign-in did not return a valid credential. Please try again.",
};

const authHeaders = () => {
  const t = getToken();
  return t
    ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function LinkGooglePage() {
  const location = useLocation();
  const navigate = useNavigate();

  // linkage: null = loading; { linked, googleEmail?, linkedAt?, unknown? }
  const [linkage, setLinkage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [toast, setToast] = useState("");

  const fetchLinkage = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch(`${API_URL}/me/link-google`, {
        method: "GET",
        headers: authHeaders(),
      });
      if (res.status === 404) {
        // Endpoint not deployed yet — render unknown, still allow linking.
        setLinkage({ linked: false, unknown: true });
        return;
      }
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${body ? `: ${body.slice(0, 200)}` : ""}`);
      }
      const data = await res.json().catch(() => ({}));
      setLinkage({
        linked: !!data?.linked,
        googleEmail: data?.googleEmail || null,
        linkedAt: data?.linkedAt || null,
      });
    } catch (e) {
      setLoadError(e.message || "Failed to load Google link status");
      setLinkage({ linked: false, unknown: true });
    }
  }, []);

  useEffect(() => {
    fetchLinkage();
  }, [fetchLinkage]);

  // Auto-redirect banner target (Login already forwards here after
  // password sign-in when ?linkGoogle=1 was set) — clean the marker
  // out of the URL so refreshing the page does not repeat the toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("fromLinkGoogle")) {
      setToast(
        "You are signed in. Click Link Google below to connect your Google account."
      );
      params.delete("fromLinkGoogle");
      const next = params.toString();
      navigate(
        { pathname: location.pathname, search: next ? `?${next}` : "" },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    const googleIdToken = credentialResponse?.credential;
    if (!googleIdToken) {
      setErrorCode("invalid_token");
      setErrorMsg(LINK_ERROR_COPY.invalid_token);
      return;
    }
    setSubmitting(true);
    setErrorCode(null);
    setErrorMsg(null);
    try {
      const res = await fetch(`${API_URL}/me/link-google`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ googleIdToken }),
      });
      if (res.status === 204 || res.ok) {
        setToast("Google linked successfully");
        await fetchLinkage();
        return;
      }
      const text = await res.text().catch(() => "");
      let parsed = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        /* non-JSON body */
      }
      const code = parsed?.error;
      if (code && LINK_ERROR_COPY[code]) {
        setErrorCode(code);
        setErrorMsg(LINK_ERROR_COPY[code]);
      } else {
        setErrorCode("unknown");
        setErrorMsg(
          parsed?.message ||
            parsed?.reason ||
            `Google link failed (HTTP ${res.status})`
        );
      }
    } catch (e) {
      setErrorCode("unknown");
      setErrorMsg(e.message || "Google link failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleError = () => {
    setErrorCode("invalid_token");
    setErrorMsg(LINK_ERROR_COPY.invalid_token);
  };

  const loading = linkage === null;

  return (
    <Box
      sx={{
        px: 3,
        py: 3,
        maxWidth: 720,
        mx: "auto",
      }}
      data-testid="link-google-page"
    >
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
        Security
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Manage sign-in methods and account security options.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Google sign-in
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Link your Google account so you can sign in with either your
                password or Google.
              </Typography>
            </Box>

            <Box sx={{ minWidth: 180, textAlign: { xs: "left", sm: "right" } }}>
              {loading ? (
                <Chip label="Loading…" size="small" data-testid="link-google-loading" />
              ) : linkage.linked ? (
                <Chip
                  color="success"
                  icon={<CheckCircleOutlineIcon />}
                  label="Linked"
                  size="small"
                  data-testid="link-google-linked-chip"
                />
              ) : (
                <Chip
                  color="default"
                  icon={<LinkOffIcon />}
                  label={linkage.unknown ? "Unknown" : "Not linked"}
                  size="small"
                  data-testid="link-google-unlinked-chip"
                />
              )}
            </Box>
          </Stack>

          {loadError && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="link-google-load-error">
              {loadError}
            </Alert>
          )}

          {!loading && linkage.linked && (
            <Box sx={{ mt: 2 }} data-testid="link-google-linked-detail">
              <Typography variant="body2">
                Linked as{" "}
                <strong data-testid="link-google-linked-email">
                  {linkage.googleEmail || "(unknown Google email)"}
                </strong>
                {linkage.linkedAt && (
                  <>
                    {" "}
                    on{" "}
                    <span data-testid="link-google-linked-date">
                      {formatDate(linkage.linkedAt)}
                    </span>
                  </>
                )}
                .
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">
              {linkage?.linked ? "Re-link Google" : "Link Google"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Signs you in to Google in a popup and exchanges the ID token
              with the platform. Nothing else changes about your account —
              your password still works.
            </Typography>
            <Box data-testid="link-google-button-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text={linkage?.linked ? "signin_with" : "continue_with"}
              />
            </Box>
            {submitting && (
              <Typography variant="body2" color="text.secondary">
                Linking Google…
              </Typography>
            )}

            {errorMsg && (
              <Alert
                severity="error"
                data-testid={`link-google-error-${errorCode || "unknown"}`}
                onClose={() => {
                  setErrorMsg(null);
                  setErrorCode(null);
                }}
              >
                {errorMsg}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={!!toast}
        autoHideDuration={4500}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setToast("")}
          data-testid="link-google-toast"
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
